import { useState, useMemo, useEffect, useCallback } from 'react';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Search,
  X,
  ExternalLink,
  Download,
  FileSpreadsheet,
  Loader2,
  Headphones,
  Sun,
  Moon,
  Sunrise,
  Sunset,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { utils, writeFile } from 'xlsx';

import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAvailabilityLeads } from '@/hooks/useAvailabilityLeads';
import { useCampaigns } from '@/hooks/useCampaigns';
import { usePageTitle } from '@/hooks/usePageTitle';
import { availabilityLeadsApi } from '@/lib/api/availability-leads.api';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime } from '@/utils/format';

import type { AvailabilityLeadQuery, CallWithContact } from '@repo/shared-types';

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 records' },
  { value: 25, label: '25 records' },
  { value: 50, label: '50 records' },
  { value: 100, label: '100 records' },
  { value: null, label: 'All records' },
];

interface AvailabilitySlot {
  date: string;
  shift_type: 'day' | 'night' | 'morning' | 'evening';
}

const getSlots = (availabilityData?: Record<string, unknown> | null): AvailabilitySlot[] => {
  if (!availabilityData) return [];
  const slots = availabilityData.availability_slots;
  if (!Array.isArray(slots)) return [];
  return slots.filter(
    (s): s is AvailabilitySlot =>
      typeof s === 'object' &&
      s !== null &&
      typeof s.date === 'string' &&
      typeof s.shift_type === 'string',
  );
};

const SHIFT_CONFIG: Record<string, { label: string; icon: typeof Sun; className: string }> = {
  morning: {
    label: 'Morning',
    icon: Sunrise,
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  day: {
    label: 'Day',
    icon: Sun,
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  },
  evening: {
    label: 'Evening',
    icon: Sunset,
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  },
  night: {
    label: 'Night',
    icon: Moon,
    className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  },
};

const ShiftBadge = ({ shiftType }: { shiftType: string }) => {
  const config = SHIFT_CONFIG[shiftType] || {
    label: shiftType,
    icon: Sun,
    className: 'bg-gray-100 text-gray-800',
  };
  const Icon = config.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        config.className,
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

const formatSlotDate = (dateStr: string): string => {
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return dateStr;
  }
};

interface jsPDFWithAutoTable extends jsPDF {
  lastAutoTable: { finalY: number };
}

const getAdditionalNotes = (availabilityData?: Record<string, unknown> | null): string | null => {
  if (!availabilityData) return null;
  const notes = availabilityData.additional_notes;
  if (typeof notes === 'string' && notes.trim()) return notes.trim();
  return null;
};

const getSmsMessage = (lead: CallWithContact): string | null => {
  // Backend returns at most 1 AVAILABILITY_CONFIRMATION SMS, sorted by sent_at desc
  return lead.sent_sms?.[0]?.message_body || null;
};

const formatSlotsForExport = (availabilityData?: Record<string, unknown> | null): string => {
  const slots = getSlots(availabilityData);
  if (slots.length === 0) return '-';
  // Group by date
  const byDate: Record<string, string[]> = {};
  for (const slot of slots) {
    if (!byDate[slot.date]) byDate[slot.date] = [];
    byDate[slot.date].push(slot.shift_type.charAt(0).toUpperCase() + slot.shift_type.slice(1));
  }
  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, shifts]) => `${formatSlotDate(date)}: ${shifts.join(', ')}`)
    .join(' | ');
};

const formatShiftTypesForExport = (availabilityData?: Record<string, unknown> | null): string => {
  const slots = getSlots(availabilityData);
  if (slots.length === 0) return '-';
  const unique = [
    ...new Set(slots.map((s) => s.shift_type.charAt(0).toUpperCase() + s.shift_type.slice(1))),
  ];
  return unique.join(', ');
};

function buildLeadRows(leads: CallWithContact[], baseUrl: string) {
  return leads.map((lead) => ({
    row: [
      lead.contact?.name || lead.contact_id || 'Unknown',
      lead.phone_number || lead.contact?.phone || '-',
      formatSlotsForExport(lead.availability_data),
      formatShiftTypesForExport(lead.availability_data),
      getAdditionalNotes(lead.availability_data) || '-',
      lead.campaign?.name || '-',
      getSmsMessage(lead) || '-',
      formatDateTime(lead.executed_at),
      `${baseUrl}/calls/${lead.id}`,
    ],
    callId: lead.id,
  }));
}

function renderLeadsTable(
  doc: jsPDFWithAutoTable,
  leads: CallWithContact[],
  startY: number,
  headColor: [number, number, number],
  baseUrl: string,
) {
  const head = [
    [
      'Carer Name',
      'Phone',
      'Availability Dates',
      'Shift Types',
      'Notes',
      'Campaign',
      'SMS Sent',
      'Call Date',
      'Recording',
    ],
  ];
  const built = buildLeadRows(leads, baseUrl);

  autoTable(doc, {
    startY,
    head,
    body: built.map((b) => b.row),
    theme: 'striped',
    headStyles: { fillColor: headColor, halign: 'left' },
    styles: { fontSize: 7 },
    columnStyles: {
      2: { cellWidth: 40 },
      4: { cellWidth: 30, fontStyle: 'italic' },
      6: { cellWidth: 35, fontStyle: 'italic' },
      8: { textColor: [37, 99, 235], cellWidth: 25 },
    },
    didDrawCell: (data) => {
      if (data.section === 'body' && data.column.index === 8) {
        const callId = built[data.row.index]?.callId;
        if (callId) {
          doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
            url: `${baseUrl}/calls/${callId}`,
          });
        }
      }
    },
  });
}

async function exportLeadsToPdf(filters: {
  search?: string;
  campaign_id?: string;
  prioritize_hr?: boolean;
  executedAtFrom?: Date;
  executedAtTo?: Date;
}) {
  const leads = await availabilityLeadsApi.getAvailabilityLeads(filters);

  const doc = new jsPDF() as jsPDFWithAutoTable;
  const pageWidth = doc.internal.pageSize.getWidth();
  const baseUrl = window.location.origin;

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Availability Leads', pageWidth / 2, 20, { align: 'center' });

  // Subtitle: HR prioritization indicator
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(
    filters.prioritize_hr ? 34 : 100,
    filters.prioritize_hr ? 139 : 100,
    filters.prioritize_hr ? 34 : 100,
  );
  doc.text(
    filters.prioritize_hr ? 'Human review prioritized' : 'Based on AI review data',
    pageWidth / 2,
    27,
    { align: 'center' },
  );

  // Generated date
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generated: ${formatDate(new Date())}`, pageWidth / 2, 34, { align: 'center' });

  let currentY = 44;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text(`Availability Leads (${leads.pagination.total})`, 14, currentY);
  currentY += 5;

  if (leads.data.length > 0) {
    renderLeadsTable(doc, leads.data, currentY, [34, 139, 34], baseUrl);
  } else {
    currentY += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('No leads found.', 14, currentY);
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
      align: 'center',
    });
  }

  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`availability_leads_${dateStr}.pdf`);
}

async function exportLeadsToCsv(filters: {
  search?: string;
  campaign_id?: string;
  prioritize_hr?: boolean;
  executedAtFrom?: Date;
  executedAtTo?: Date;
}) {
  const leads = await availabilityLeadsApi.getAvailabilityLeads(filters);

  const baseUrl = window.location.origin;
  const mapLeadToRow = (lead: CallWithContact) => ({
    'Carer Name': lead.contact?.name || lead.contact_id || 'Unknown',
    Phone: lead.phone_number || lead.contact?.phone || '-',
    'Availability Dates': formatSlotsForExport(lead.availability_data),
    'Shift Types': formatShiftTypesForExport(lead.availability_data),
    Notes: getAdditionalNotes(lead.availability_data) || '-',
    Campaign: lead.campaign?.name || '-',
    'SMS Sent': getSmsMessage(lead) || '-',
    'Call Date': formatDate(lead.executed_at),
    'Recording Link': `${baseUrl}/calls/${lead.id}`,
  });

  const rows = leads.data.map((lead) => mapLeadToRow(lead));

  const ws = utils.json_to_sheet(rows);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Leads');

  const dateStr = new Date().toISOString().slice(0, 10);
  writeFile(wb, `availability_leads_${dateStr}.csv`);
}

export const AvailabilityLeadsPage = () => {
  usePageTitle('Availability Leads');
  const [searchParams, setSearchParams] = useSearchParams();

  const campaignId = searchParams.get('campaign_id') || '';
  const page = Number(searchParams.get('page')) || 1;
  const limitParam = searchParams.get('limit');
  const limit = limitParam === 'all' ? undefined : Number(limitParam) || 10;
  const prioritizeHr = searchParams.get('prioritize_hr') === 'true';

  // Date range filters
  const executedAtFrom = searchParams.get('executedAtFrom') || '';
  const executedAtTo = searchParams.get('executedAtTo') || '';

  const { data: campaignsData } = useCampaigns({ limit: 100 });

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const searchChanged = debouncedSearch !== searchInput;
      setDebouncedSearch(searchInput);
      updateParams({
        search: searchInput || null,
        ...(searchChanged && { page: null }),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams, debouncedSearch]);

  const query = useMemo(() => {
    const q: Partial<AvailabilityLeadQuery> = { page };
    if (limit !== undefined) q.limit = limit;
    if (debouncedSearch) q.search = debouncedSearch;
    if (campaignId) q.campaign_id = campaignId;
    if (prioritizeHr) q.prioritize_hr = true;
    if (executedAtFrom) {
      const partsFrom = executedAtFrom.split('-');
      if (partsFrom.length === 3) {
        const [yearFrom, monthFrom, dayFrom] = partsFrom.map((part) => Number(part));
        if (!Number.isNaN(yearFrom) && !Number.isNaN(monthFrom) && !Number.isNaN(dayFrom)) {
          const fromDate = new Date(yearFrom, monthFrom - 1, dayFrom, 0, 0, 0, 0);
          if (!Number.isNaN(fromDate.getTime())) {
            q.executedAtFrom = fromDate;
          }
        }
      }
    }
    if (executedAtTo) {
      const partsTo = executedAtTo.split('-');
      if (partsTo.length === 3) {
        const [yearTo, monthTo, dayTo] = partsTo.map((part) => Number(part));
        if (!Number.isNaN(yearTo) && !Number.isNaN(monthTo) && !Number.isNaN(dayTo)) {
          const toDate = new Date(yearTo, monthTo - 1, dayTo, 23, 59, 59, 999);
          if (!Number.isNaN(toDate.getTime())) {
            q.executedAtTo = toDate;
          }
        }
      }
    }
    return q;
  }, [debouncedSearch, campaignId, page, limit, prioritizeHr, executedAtFrom, executedAtTo]);

  const { data, isLoading } = useAvailabilityLeads(query);
  const leads = data?.data ?? [];
  const pagination = data?.pagination;

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const exporting = exportingPdf || exportingCsv;

  const hasActiveFilters = searchInput || campaignId || executedAtFrom || executedAtTo;

  const buildExportFilters = useCallback(() => {
    const filters: {
      search?: string;
      campaign_id?: string;
      prioritize_hr?: boolean;
      executedAtFrom?: Date;
      executedAtTo?: Date;
    } = {};
    if (debouncedSearch) filters.search = debouncedSearch;
    if (campaignId) filters.campaign_id = campaignId;
    if (prioritizeHr) filters.prioritize_hr = true;
    if (query.executedAtFrom) filters.executedAtFrom = query.executedAtFrom;
    if (query.executedAtTo) filters.executedAtTo = query.executedAtTo;
    return filters;
  }, [debouncedSearch, campaignId, prioritizeHr, query.executedAtFrom, query.executedAtTo]);

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      await exportLeadsToPdf(buildExportFilters());
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      await exportLeadsToCsv(buildExportFilters());
    } finally {
      setExportingCsv(false);
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      const currentLimit = prev.get('limit');
      if (currentLimit) next.set('limit', currentLimit);
      const currentHr = prev.get('prioritize_hr');
      if (currentHr) next.set('prioritize_hr', currentHr);
      return next;
    });
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
          Availability Leads
        </p>
        <h1 className="text-3xl font-bold tracking-tight">Availability Leads</h1>
        <p className="text-muted-foreground max-w-3xl">
          Conversations where availability was captured and saved to the booking system.
        </p>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Leads</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={exporting}>
                {exportingCsv ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" />
                )}
                {exportingCsv ? 'Exporting...' : 'Export to CSV'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPdf} disabled={exporting}>
                {exportingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {exportingPdf ? 'Exporting...' : 'Export to PDF'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-50 flex-1">
              <label className="text-muted-foreground mb-1 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by phone..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
              </div>
            </div>
            <div className="min-w-45">
              <label
                htmlFor="campaign-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Campaign
              </label>
              <select
                id="campaign-filter"
                value={campaignId}
                onChange={(e) => updateParams({ campaign_id: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All campaigns</option>
                {campaignsData?.data.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label
                htmlFor="from-date"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                From
              </label>
              <input
                id="from-date"
                type="date"
                value={executedAtFrom}
                onChange={(e) =>
                  updateParams({ executedAtFrom: e.target.value || null, page: null })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              />
            </div>
            <div className="min-w-[140px]">
              <label
                htmlFor="to-date"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                To
              </label>
              <input
                id="to-date"
                type="date"
                value={executedAtTo}
                onChange={(e) => updateParams({ executedAtTo: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              />
            </div>
            <div className="min-w-[120px]">
              <label
                htmlFor="limit-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Per Page
              </label>
              <select
                id="limit-filter"
                value={limit === undefined ? 'all' : limit}
                onChange={(e) =>
                  updateParams({
                    limit: e.target.value === 'all' ? 'all' : e.target.value,
                    page: null,
                  })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option.value ?? 'all'} value={option.value ?? 'all'}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <input
                id="prioritize-hr"
                type="checkbox"
                checked={prioritizeHr}
                onChange={(e) =>
                  updateParams({
                    prioritize_hr: e.target.checked ? 'true' : null,
                    page: null,
                  })
                }
                className="text-primary focus:ring-primary h-4 w-4 rounded border"
              />
              <label htmlFor="prioritize-hr" className="text-sm font-medium whitespace-nowrap">
                Prioritize Human Review
              </label>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading leads...</p>
              </div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No leads found. {hasActiveFilters ? 'Try adjusting your filters.' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="border-b px-3 py-2">Carer Name</th>
                    <th className="border-b px-3 py-2">Availability Dates</th>
                    <th className="border-b px-3 py-2">Shift Types</th>
                    <th className="border-b px-3 py-2">Notes</th>
                    <th className="border-b px-3 py-2">Campaign</th>
                    <th className="border-b px-3 py-2">SMS Sent</th>
                    <th className="border-b px-3 py-2">Call Date</th>
                    <th className="border-b px-3 py-2 text-right">Recording</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead: CallWithContact) => {
                    const slots = getSlots(lead.availability_data);
                    // Group slots by date for cleaner display
                    const slotsByDate = slots.reduce<Record<string, string[]>>((acc, slot) => {
                      if (!acc[slot.date]) acc[slot.date] = [];
                      acc[slot.date].push(slot.shift_type);
                      return acc;
                    }, {});
                    const sortedDates = Object.keys(slotsByDate).sort();
                    // Unique shift types across all slots
                    const uniqueShifts = [...new Set(slots.map((s) => s.shift_type))];

                    return (
                      <tr key={lead.id} className="hover:bg-muted/50 border-b transition-colors">
                        <td className="px-3 py-2">
                          <div className="font-medium">{lead.contact?.name || 'Unknown'}</div>
                          <div className="text-muted-foreground text-xs">
                            {lead.phone_number || lead.contact?.phone || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {sortedDates.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {sortedDates.map((date) => (
                                <div key={date} className="flex items-center gap-2 text-sm">
                                  <span className="text-foreground font-medium">
                                    {formatSlotDate(date)}
                                  </span>
                                  <div className="flex gap-1">
                                    {slotsByDate[date].map((shift, i) => (
                                      <ShiftBadge key={`${date}-${shift}-${i}`} shiftType={shift} />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {uniqueShifts.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {uniqueShifts.map((shift) => (
                                <ShiftBadge key={shift} shiftType={shift} />
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {(() => {
                            const notes = getAdditionalNotes(lead.availability_data);
                            return notes ? (
                              <span className="text-muted-foreground italic" title={notes}>
                                {notes.length > 50 ? `${notes.slice(0, 50)}...` : notes}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            );
                          })()}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-sm">
                          {lead.campaign?.name || '-'}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {(() => {
                            const sms = getSmsMessage(lead);
                            return sms ? (
                              <span className="text-muted-foreground italic" title={sms}>
                                {sms.length > 60 ? `${sms.slice(0, 60)}...` : sms}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            );
                          })()}
                        </td>
                        <td className="text-muted-foreground px-3 py-2 text-sm">
                          {formatDateTime(lead.executed_at)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Link
                            to={`/calls/${lead.id}`}
                            className="text-primary hover:text-primary/80 inline-flex items-center gap-1 text-xs font-medium"
                          >
                            <Headphones className="h-3.5 w-3.5" />
                            Listen
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <div className="mt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
                resourceName="leads"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
